// Final closeout invoice helpers (Prompt 10 of the billing-flow series).
// Wraps the compute_final_invoice_preview + complete_case_and_finalize RPCs,
// the invoices table reads, and the Edge Function invoke for manual
// "Regenerate" flows.
//
// Read helpers return null on miss; write helpers throw with the RPC's
// error message so the caller can surface it inline.

import { supabase } from "./supabase";

export type FinalInvoiceLine = {
  line_item_label: string;
  unit_count: number;
  unit_rate_cents: number;
  subtotal_cents: number;
};

export type InvoiceRow = {
  id: string;
  competition_id: string;
  quote_id: string | null;
  kind: "initial" | "final";
  status: "pending" | "paid" | "failed" | "voided";
  amount_cents: number;
  tax_cents: number;
  total_cents: number;
  stripe_customer_id: string | null;
  stripe_invoice_id: string | null;
  stripe_invoice_hosted_url: string | null;
  stripe_invoice_pdf_url: string | null;
  stripe_receipt_url: string | null;
  due_date: string | null;
  net_terms_days: number | null;
  sent_at: string | null;
  paid_at: string | null;
  failed_at: string | null;
  voided_at: string | null;
  failure_reason: string | null;
  void_reason: string | null;
  created_at: string;
  updated_at: string;
};

export type CompleteCaseInput = {
  caseId: string;
  studentCount: number;
  submissionCount: number;
  teamPresentationCount?: number | null;
  completionNotes?: string | null;
  netTermsDays?: number;
};

export async function computeFinalInvoicePreview(input: {
  caseId: string;
  studentCount: number;
  submissionCount: number;
  teamPresentationCount?: number | null;
}): Promise<FinalInvoiceLine[]> {
  const { data, error } = await supabase.rpc("compute_final_invoice_preview", {
    p_case_id: input.caseId,
    p_student_count: input.studentCount,
    p_submission_count: input.submissionCount,
    p_team_presentation_count: input.teamPresentationCount ?? null,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as FinalInvoiceLine[];
}

export async function completeCaseAndFinalize(input: CompleteCaseInput): Promise<string> {
  const { data, error } = await supabase.rpc("complete_case_and_finalize", {
    p_case_id: input.caseId,
    p_student_count: input.studentCount,
    p_submission_count: input.submissionCount,
    p_team_presentation_count: input.teamPresentationCount ?? null,
    p_completion_notes: input.completionNotes ?? null,
    p_net_terms_days: input.netTermsDays ?? 30,
  });
  if (error) throw new Error(error.message);
  if (typeof data !== "string") throw new Error("complete_case_and_finalize did not return a UUID");
  return data;
}

/** Look up the most recent final invoice for a case. Returns null if
 *  none exists yet (case hasn't been finalized). */
export async function getFinalInvoiceForCase(competitionId: string): Promise<InvoiceRow | null> {
  const { data, error } = await supabase
    .from("invoices")
    .select("*")
    .eq("competition_id", competitionId)
    .eq("kind", "final")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return data as InvoiceRow;
}

/** Invoke the create-stripe-final-invoice Edge Function manually (e.g.
 *  the admin's "Regenerate" button when the auto-trigger failed). */
export async function regenerateFinalInvoice(competitionId: string): Promise<InvoiceRow | null> {
  const { data, error } = await supabase.functions.invoke("create-stripe-final-invoice", {
    body: { competition_id: competitionId, manual: true },
  });
  if (error) throw new Error(error.message);
  const payload = (data ?? {}) as { invoice_id?: string; error?: string };
  if (payload.error) throw new Error(payload.error);
  if (!payload.invoice_id) return null;
  return await getFinalInvoiceForCase(competitionId);
}

/** Compute existing rough counts for the admin "Complete case" UI's
 *  auto-fill defaults. Returns a best-effort count of registered
 *  students + submitted submissions for the case. Both fall back to 0
 *  if the underlying RLS hides the rows from this admin (shouldn't
 *  happen since admins read everything, but defensive). */
export async function getCaseCountsForCompletion(competitionId: string): Promise<{
  registered_students: number;
  total_submissions: number;
}> {
  const [studRes, subRes] = await Promise.all([
    supabase
      .from("registrations")
      .select("id", { count: "exact", head: true })
      .eq("competition_id", competitionId)
      .in("status", ["registered", "submitted"]),
    supabase
      .from("submissions")
      .select("id", { count: "exact", head: true })
      .eq("competition_id", competitionId),
  ]);
  return {
    registered_students: studRes.count ?? 0,
    total_submissions: subRes.count ?? 0,
  };
}

/** List cases that are ready for finalization but haven't been
 *  finalized yet — feeds the auto-suggest banner on the admin
 *  approvals page. Threshold: closes_at < now() - 7 days, status in
 *  the running set, no final invoice yet. */
export async function listCasesAwaitingFinalization(): Promise<
  Array<{
    id: string;
    name: string;
    company_name: string | null;
    engagement_model: string;
    closes_at: string | null;
    days_overdue: number;
  }>
> {
  const sevenDaysAgoIso = new Date(Date.now() - 7 * 86_400_000).toISOString();
  const { data, error } = await supabase
    .from("competitions")
    .select("id, name, company_id, engagement_model, closes_at, companies:company_id(name)")
    .in("status", ["open", "judging", "closed"])
    .lt("closes_at", sevenDaysAgoIso);
  if (error || !data) return [];

  // Exclude cases that already have a final invoice (pending/paid).
  const compIds = data.map((c) => c.id as string);
  let alreadyInvoiced = new Set<string>();
  if (compIds.length > 0) {
    const { data: invs } = await supabase
      .from("invoices")
      .select("competition_id, status")
      .eq("kind", "final")
      .in("competition_id", compIds)
      .in("status", ["pending", "paid"]);
    alreadyInvoiced = new Set((invs ?? []).map((i) => i.competition_id as string));
  }

  const now = Date.now();
  return data
    .filter((c) => !alreadyInvoiced.has(c.id as string))
    .map((c) => {
      const company = Array.isArray(c.companies) ? c.companies[0] : c.companies;
      const closesAtIso = (c.closes_at as string | null) ?? null;
      const closesAtMs = closesAtIso ? new Date(closesAtIso).getTime() : 0;
      const daysOverdue = closesAtMs ? Math.floor((now - closesAtMs) / 86_400_000) : 0;
      return {
        id: c.id as string,
        name: c.name as string,
        company_name: (company?.name as string | undefined) ?? null,
        engagement_model: c.engagement_model as string,
        closes_at: closesAtIso,
        days_overdue: daysOverdue,
      };
    })
    .sort((a, b) => b.days_overdue - a.days_overdue);
}
